import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  MicrophoneIcon, 
  ComputerDesktopIcon,
  PlayIcon,
  ClockIcon,
  ChartBarIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import clsx from 'clsx'

import Button from '@/components/ui/Button'
import ProgressBar from '@/components/ui/ProgressBar'
import UploadProgressModal from '@/components/ui/UploadProgressModal'
import { useAppSelector } from '@/hooks/redux'
import { useMediaPermissions } from '@/hooks/useMediaPermissions'
import { useProperVAD } from '@/hooks/useProperVAD'
import { useInterview } from '@/hooks/useInterview'
import { useChunkedRecording } from '@/hooks/useChunkedRecording'
import { useSpeechRecording } from '@/hooks/useSpeechRecording'
import { useCompetencyBasedRecording } from '@/hooks/useCompetencyBasedRecording'
import { useTheme } from '@/contexts/ThemeContext'
import { apiUrl } from '@/utils/api'

interface CompetencyArea {
  id: string
  name: string
  progress: number
  questionsAsked: number
  totalQuestions: number
  completed: boolean
}

const InterviewPage: React.FC = () => {
  const navigate = useNavigate()
  const { candidateName, position, isAuthenticated } = useAppSelector(state => state.auth)
  const { fontSize } = useTheme()
  
  // Text-only: ignore any stored speech preference (speech UI disabled)
  const [inputMode] = useState<'speech' | 'text'>('text')

  useEffect(() => {
    // Clear stale speech preference from older sessions
    if (localStorage.getItem('interviewInputType') === 'speech') {
      localStorage.setItem('interviewInputType', 'text')
    }
  }, [])
  
  const {
    streams,
    stopAllStreams,
    initializeStreamsFromPermissions,
    requestScreenShare,
    setOnScreenSharingEnded
  } = useMediaPermissions()

  const [messageInput, setMessageInput] = useState('')
  const [isAutoStarted, setIsAutoStarted] = useState(false)
  const [isRequestingScreenShare, setIsRequestingScreenShare] = useState(false)
  
  // Upload progress tracking
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [videoUploadStatus, setVideoUploadStatus] = useState<'pending' | 'uploading' | 'success' | 'error'>('pending')
  const [reportGenerationStatus, setReportGenerationStatus] = useState<'pending' | 'generating' | 'success' | 'error'>('pending')
  const [uploadError, setUploadError] = useState<string>('')
  const [currentUploadAttempt, setCurrentUploadAttempt] = useState(1)
  
  // Track if user switches tabs (affects recording)
  const [isTabVisible, setIsTabVisible] = useState(true)
  
  // Audio warning for tab switching
  const warningAudioRef = useRef<HTMLAudioElement | null>(null)
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const competencyContainerRef = useRef<HTMLDivElement>(null)
  const currentCompetencyRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Get session ID for recording (should be created by Welcome page)
  const [sessionId, setSessionId] = useState<string | null>(() => {
    const id = localStorage.getItem('interview-session-id')
    if (!id) {
      console.error('No interview session ID found in InterviewPage')
    }
    return id
  })

  // Initialize interview hook first so we can access state (must be before any early returns)
  const {
    state,
    isLoading,
    error,
    startInterview: startInterviewHook,
    sendMessage,
    endInterview
  } = useInterview()

  const fontClass =
    fontSize === 'small'
      ? 'font-small'
      : fontSize === 'large'
      ? 'font-large'
      : 'font-medium'

  // Speech recording hook (for speech-to-text) - needs state to be available
  const speechRecording = useSpeechRecording({
    enabled: inputMode === 'speech' && state.isActive,
    onTranscriptionReceived: async (text) => {
      console.log('Transcription received:', text)
      // Automatically send the transcribed message
      if (text.trim()) {
        try {
          await sendMessage(text.trim())
          // toast.success('Message sent automatically')
        } catch (error) {
          console.error('Failed to send transcribed message:', error)
          toast.error('Failed to send message')
        }
      }
    },
    onError: (error) => {
      console.error('Speech recording error:', error)
      toast.error(error)
    }
  })

  const {
    isSpeaking,
    startListening: startVAD,
    stopListening: stopVAD
  } = useProperVAD({
    enableSpeechRecording: inputMode === 'speech',
    onStartRecording: () => {
      if (inputMode === 'speech') {
        speechRecording.startRecording()
      }
    },
    onStopRecording: () => {
      if (inputMode === 'speech') {
        speechRecording.stopRecording()
      }
    }
  })

  // Competency-based recording hook - DISABLED for now
  // const competencyRecording = useCompetencyBasedRecording({
  //   sessionId,
  //   onChunkUploaded: (competency, chunkIndex) => {
  //     console.log(`Uploaded video chunk for ${competency}, chunk ${chunkIndex}`)
  //     toast.success(`Video chunk uploaded: ${competency}`)
  //   },
  //   onUploadError: (error, competency) => {
  //     console.error(`Failed to upload video chunk for ${competency}:`, error)
  //     toast.error(`Failed to upload video for ${competency}: ${error.message}`)
  //   }
  // })

  // Placeholder for competency recording (disabled)
  const competencyRecording = {
    isRecording: false,
    currentCompetency: null,
    startCompetencyRecording: () => {},
    stopRecording: async () => {},
    switchCompetency: () => {}
  }

  // Single video recording fallback (for final video)
  const videoRecordingRef = useRef<{
    mediaRecorder: MediaRecorder | null
    recordedChunks: Blob[]
    stream: MediaStream | null
    finalVideoBlob: Blob | null // Store the final video blob for retry attempts
  }>({
    mediaRecorder: null,
    recordedChunks: [],
    stream: null,
    finalVideoBlob: null
  })

  // Create combined video stream using canvas composition
  const createCombinedVideoStream = async (screenStream: MediaStream, cameraStream: MediaStream): Promise<MediaStream> => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d', { alpha: false })!
    
    // Set canvas dimensions (highly optimized for minimal file size)
    canvas.width = 960
    canvas.height = 540
    
    // Configure context for efficient rendering
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'medium'
    
    // Create video elements for rendering
    const screenVideo = document.createElement('video')
    const cameraVideo = document.createElement('video')
    
    screenVideo.srcObject = screenStream
    cameraVideo.srcObject = cameraStream
    
    screenVideo.autoplay = true
    screenVideo.muted = true
    cameraVideo.autoplay = true
    cameraVideo.muted = true
    
    await Promise.all([
      new Promise(resolve => { screenVideo.onloadedmetadata = () => resolve(null) }),
      new Promise(resolve => { cameraVideo.onloadedmetadata = () => resolve(null) })
    ])
    
    await Promise.all([screenVideo.play(), cameraVideo.play()])
    
    // Position and size for picture-in-picture camera overlay (minimal size)
    const cameraWidth = 200  // Minimal size maintaining 16:9 ratio
    const cameraHeight = 112 // Maintains 16:9 aspect ratio (200/112 = 1.79)
    const cameraX = canvas.width - cameraWidth - 25 // 25px margin from right
    const cameraY = 25 // 25px margin from top
    
    // Render frames continuously
    const renderFrame = () => {
      if (!ctx) return
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      
      // Draw screen share (full background)
      if (screenVideo.videoWidth > 0 && screenVideo.videoHeight > 0) {
        ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height)
      }
      
      // Draw camera feed (picture-in-picture overlay)
      if (cameraVideo.videoWidth > 0 && cameraVideo.videoHeight > 0) {
        // Add border to camera overlay
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 2
        ctx.strokeRect(cameraX - 1, cameraY - 1, cameraWidth + 2, cameraHeight + 2)
        
        // Draw camera video
        ctx.drawImage(cameraVideo, cameraX, cameraY, cameraWidth, cameraHeight)
      }
      
      requestAnimationFrame(renderFrame)
    }
    
    renderFrame()
    
    // Get video track from canvas with minimal frame rate for smallest file size
    const canvasStream = canvas.captureStream(15) // 15 FPS for minimal file size
    const videoTrack = canvasStream.getVideoTracks()[0]
    
    // Configure video track settings for optimal quality
    if (videoTrack.getSettings) {
      const settings = videoTrack.getSettings()
      console.log('Canvas video track settings:', settings)
    }
    
    // Get audio track from camera (microphone)
    const audioTracks = cameraStream.getAudioTracks()
    
    // Create combined stream
    const combinedTracks = [videoTrack, ...audioTracks]
    const combinedStream = new MediaStream(combinedTracks)
    
    console.log('Canvas-based combined stream created with video from canvas and audio from camera')
    
    return combinedStream
  }

  // Single video recording functions
  const startSingleVideoRecording = (stream: MediaStream) => {
    try {
      // Try higher quality codecs first, then fallback to lower quality options
      const codecOptions = [
        'video/webm;codecs=vp9,opus',    // VP9 - highest quality
        'video/webm;codecs=h264,opus',   // H.264 - widely supported  
        'video/webm;codecs=vp8,opus',    // VP8 - fallback
        'video/webm'                     // Default
      ]
      
      let selectedMimeType = 'video/webm'
      for (const mimeType of codecOptions) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType
          break
        }
      }
      
      console.log('Selected mimeType for single recording:', selectedMimeType)

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: 300000,  // 300kbps for single recording (minimal size, adequate quality)
        audioBitsPerSecond: 48000,   // 48kbps for audio (minimal size, acceptable speech quality)
      })

      videoRecordingRef.current.recordedChunks = []
      videoRecordingRef.current.mediaRecorder = mediaRecorder
      videoRecordingRef.current.stream = stream

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoRecordingRef.current.recordedChunks.push(event.data)
        }
      }

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event)
        toast.error('Video recording error occurred')
      }

      mediaRecorder.start()
      console.log('Single video recording started')

    } catch (error) {
      console.error('Failed to start single video recording:', error)
      toast.error('Failed to start video recording')
    }
  }

    const stopSingleVideoRecordingAndGenerateReport = async () => {
        return new Promise<void>((resolve, reject) => {
            const mediaRecorder = videoRecordingRef.current.mediaRecorder

            if (!mediaRecorder || mediaRecorder.state === 'inactive') {
                console.warn('No active recording to stop')
                resolve()
                return
            }

            // Set up the stop handler
            mediaRecorder.onstop = async () => {
                try {
                    console.log('MediaRecorder stopped, processing final video...')

                    // Show the upload progress modal
                    setIsUploadModalOpen(true)
                    setVideoUploadStatus('pending')
                    setReportGenerationStatus('pending')
                    setUploadError('')

                    // Create final video blob and store it for potential retry
                    const videoBlob = new Blob(videoRecordingRef.current.recordedChunks, {
                        type: 'video/webm'
                    })

                    // Store the video blob for retry attempts
                    videoRecordingRef.current.finalVideoBlob = videoBlob
                    console.log('Video blob stored for potential retries:', {
                        size: videoBlob.size,
                        type: videoBlob.type
                    })

                    console.log(`Final video size: ${videoBlob.size} bytes`)

                    // Upload video and generate comprehensive report in parallel
                    const [videoUploadResult, reportResult] = await Promise.allSettled([
                        uploadFinalVideoWithRetry(videoBlob),
                        generateComprehensiveReport()
                    ])

                    // Check if both operations succeeded
                    const videoUploadSuccess = videoUploadResult.status === 'fulfilled'
                    const reportGenerationSuccess = reportResult.status === 'fulfilled'

                    console.log(`Processing results: Video upload: ${videoUploadSuccess ? 'SUCCESS' : 'FAILED'}, Report generation: ${reportGenerationSuccess ? 'SUCCESS' : 'FAILED'}`)

                    if (videoUploadSuccess && reportGenerationSuccess) {
                        // Both succeeded - show success and navigate
                        console.log('All processing completed successfully, initiating navigation...')

                        // Wait a moment for user to see success
                        await new Promise(resolve => setTimeout(resolve, 2000))

                        // Close modal and clean up resources
                        setIsUploadModalOpen(false)
                        videoRecordingRef.current = {
                            mediaRecorder: null,
                            recordedChunks: [],
                            stream: null,
                            finalVideoBlob: null
                        }

                        // Stop media streams and VAD
                        stopVAD()
                        stopAllStreams()

                        // Navigate to completion page (session clearing happens there)
                        navigate('/completed', { replace: true })

                        resolve()
                    } else {
                        // One or both failed - keep modal open for retry
                        console.log('Some operations failed, keeping modal open for retry')

                        if (!videoUploadSuccess) {
                            console.error('Video upload failed:', videoUploadResult.reason)
                            setVideoUploadStatus('error')
                        }

                        if (!reportGenerationSuccess) {
                            console.error('Report generation failed:', reportResult.reason)
                            setReportGenerationStatus('error')
                        }

                        // Don't resolve - this keeps the modal open for retry
                        reject(new Error('Processing incomplete. Please try again.'))
                    }
                } catch (error) {
                    console.error('Error in stop handler:', error)
                    setVideoUploadStatus('error')
                    setReportGenerationStatus('error')
                    setUploadError(error instanceof Error ? error.message : 'Unknown error occurred')
                    reject(error)
                }
            }

            // Stop the recording
            mediaRecorder.stop()
        })
    }

  // Upload final video with retry mechanism
  const uploadFinalVideoWithRetry = async (videoBlob: Blob, maxRetries: number = 3): Promise<void> => {
    if (!sessionId) {
      throw new Error('No session ID available for video upload')
    }
    
    setVideoUploadStatus('uploading')
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Uploading final video, attempt ${attempt}/${maxRetries}...`)
        setCurrentUploadAttempt(attempt)
        
        const formData = new FormData()
        formData.append('video_file', videoBlob, `final_interview_${sessionId}_${Date.now()}.webm`)
        
        const uploadUrl = apiUrl(`/api/v1/interview/sessions/${sessionId}/upload-final-video`)
        
        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData
        })

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        // console.log('Final video uploaded successfully:', result)
        setVideoUploadStatus('success')
        return // Success, exit retry loop

      } catch (error) {
        console.error(`Video upload attempt ${attempt} failed:`, error)
        
        if (attempt === maxRetries) {
          // Final attempt failed
          setVideoUploadStatus('error')
          setUploadError(error instanceof Error ? error.message : 'Unknown upload error')
          throw new Error(`Failed to upload video after ${maxRetries} attempts: ${error}`)
        }
        
        // Wait before retrying (exponential backoff)
        const delayMs = Math.pow(2, attempt) * 1000 // 2s, 4s, 8s...
        console.log(`Retrying upload in ${delayMs}ms...`)
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    }
  }

  // Handle retry of processing operations
    const handleRetryProcessing = async () => {
        console.log('Retrying processing operations...')

        // Check if we have a stored video blob
        const storedVideoBlob = videoRecordingRef.current.finalVideoBlob
        if (!storedVideoBlob || storedVideoBlob.size === 0) {
            console.error('No video blob available for retry', {
                hasBlob: !!storedVideoBlob,
                blobSize: storedVideoBlob?.size || 0
            })
            toast.error('No video data available for retry. Please refresh and try again.')
            return
        }

        console.log('Video blob available for retry:', {
            size: storedVideoBlob.size,
            type: storedVideoBlob.type
        })

        // Reset status to show retry attempt
        if (videoUploadStatus === 'error') {
            setVideoUploadStatus('pending')
        }
        if (reportGenerationStatus === 'error') {
            setReportGenerationStatus('pending')
        }
        setUploadError('')

        try {
            console.log(`Retrying with stored video blob of size: ${storedVideoBlob.size} bytes`)

            // Retry failed operations using stored video blob
            const retryPromises: Promise<void>[] = []

            // Determine which operations need retrying
            const needsVideoRetry = videoUploadStatus === 'error'
            const needsReportRetry = reportGenerationStatus === 'error'

            console.log('Retry needed:', { video: needsVideoRetry, report: needsReportRetry })

            // Retry video upload if it failed
            if (needsVideoRetry) {
                console.log('Adding video upload retry to queue')
                retryPromises.push(uploadFinalVideoWithRetry(storedVideoBlob))
            }

            // Retry report generation if it failed
            if (needsReportRetry) {
                console.log('Adding report generation retry to queue')
                retryPromises.push(generateComprehensiveReport())
            }

            // If no operations need retry, we're already done
            if (retryPromises.length === 0) {
                console.log('No operations need retry, both are successful')
                toast.success('Interview data processed successfully!')

                // Wait a moment then clean up and navigate
                await new Promise(resolve => setTimeout(resolve, 1000))

                // Close modal and clean up
                setIsUploadModalOpen(false)
                videoRecordingRef.current = {
                    mediaRecorder: null,
                    recordedChunks: [],
                    stream: null,
                    finalVideoBlob: null
                }

                // Stop media streams and VAD
                stopVAD()
                stopAllStreams()

                // Navigate to completion page
                navigate('/completed', { replace: true })
                return
            }

            // Wait for all retry operations to complete
            const retryResults = await Promise.allSettled(retryPromises)

            // Check if all retries succeeded
            const allRetriesSucceeded = retryResults.every(result => result.status === 'fulfilled')

            if (allRetriesSucceeded) {
                console.log('Retry completed successfully')
                toast.success('Interview data processed successfully!')

                // Wait a moment then clean up and navigate
                await new Promise(resolve => setTimeout(resolve, 2000))

                // Close modal and clean up
                setIsUploadModalOpen(false)
                videoRecordingRef.current = {
                    mediaRecorder: null,
                    recordedChunks: [],
                    stream: null,
                    finalVideoBlob: null
                }

                // Stop media streams and VAD
                stopVAD()
                stopAllStreams()

                // Navigate to completion page
                navigate('/completed', { replace: true })
            } else {
                // Some retries still failed
                console.log('Some retry operations still failed')
                toast.error('Some operations still failed. Please try again.')

                // Log specific failures
                retryResults.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        console.error(`Retry operation ${index} failed:`, result.reason)
                    }
                })
            }

        } catch (error) {
            console.error('Retry failed:', error)
            toast.error('Retry failed. Please try again.')
        }
    }

  // Generate comprehensive interview report
  const generateComprehensiveReport = async (): Promise<void> => {
    if (!sessionId) {
      throw new Error('No session ID available for report generation')
    }
    
    setReportGenerationStatus('generating')
    
    try {
      console.log('Queueing comprehensive interview report generation...')
      
      const reportUrl = apiUrl(`/api/v1/interview/sessions/${sessionId}/queue-report`)
      
      const response = await fetch(reportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: true, message: response.statusText }))
        
        if (response.status === 404) {
          throw new Error(`Interview session not found. Report cannot be generated without interview data.`)
        } else if (response.status === 400) {
          throw new Error(`No interview interactions found. Report can only be generated after answering interview questions.`)
        }
        
        throw new Error(`Failed to queue report generation: ${errorData.message || response.statusText}`)
      }

      const result = await response.json()
      console.log('Report generation queued successfully:', result)
      
      // Report generation has been queued and will be processed in background
      
      setReportGenerationStatus('success')
      
    } catch (error) {
      console.error('Failed to queue comprehensive report generation:', error)
      setReportGenerationStatus('error')
      throw error
    }
  }

  // Initialize camera stream only (don't auto-request screen sharing)
  useEffect(() => {
    const initCameraStream = async () => {
      console.log('Interview page mounted, initializing camera stream...')
      // Only initialize camera stream, not screen sharing
      if (!streams.camera) {
        await initializeStreamsFromPermissions(false) // Don't include screen sharing
      }
    }

    // Only initialize once when component mounts
    initCameraStream()
  }, []) // Empty dependency array to run only once

  // Initialize warning audio
  useEffect(() => {
    warningAudioRef.current = new Audio('/beep-warning-6387.mp3')
    warningAudioRef.current.volume = 0.7 // Set reasonable volume
    
    return () => {
      if (warningAudioRef.current) {
        warningAudioRef.current = null
      }
    }
  }, [])

  // Monitor tab visibility to warn about recording issues and log events
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const isVisible = !document.hidden
      setIsTabVisible(isVisible)
      
      // Log tab switching event regardless of interview state for reporting
      if (sessionId) {
        try {
          const eventType = isVisible ? 'tab_switch_back' : 'tab_switch_away'
          
          const response = await fetch(apiUrl(`/api/v1/interview/sessions/${sessionId}/tab-switch`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event_type: eventType,
              timestamp: new Date().toISOString()
            })
          })
          
          if (response.ok) {
            console.log(`Tab switch event logged: ${eventType}`)
          } else {
            console.warn(`Failed to log tab switch event: ${response.statusText}`)
          }
        } catch (error) {
          console.warn('Error logging tab switch event:', error)
        }
      }
      
      // Show warning and play sound only during active interview
      if (state.isActive && videoRecordingRef.current.mediaRecorder) {
        if (!isVisible) {
          console.warn('Tab switched away - recording may be affected')
          toast.warning('Recording may be affected when switching tabs. Please stay on this page during the interview.')
          
          // Play warning sound
          if (warningAudioRef.current) {
            warningAudioRef.current.play().catch(error => {
              console.warn('Could not play warning audio:', error)
            })
          }
        } else {
          console.log('Tab became visible again')
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [state.isActive, sessionId])

  // Handle page refresh/navigation during active interview
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (state.isActive) {
        // Prevent page refresh/navigation during active interview
        const message = 'Interview in progress. Leaving will end your interview session.'
        event.preventDefault()
        event.returnValue = message
        return message
      }
    }

    const handleUnload = () => {
      // Mark session as interrupted if user leaves during active interview
      if (state.isActive && videoRecordingRef.current.mediaRecorder?.state === 'recording') {
        console.log('User left during active interview - session may be incomplete')
        // Note: We can't make async calls here, but the backend should handle cleanup of abandoned sessions
      }
    }

    // Prevent F5/Ctrl+R during interview
    const handleKeyDown = (event: KeyboardEvent) => {
      if (state.isActive) {
        // Prevent F5 (refresh)
        if (event.key === 'F5') {
          event.preventDefault()
          return false
        }
        
        // Prevent Ctrl+R/Cmd+R (refresh)
        if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
          event.preventDefault()
          return false
        }
        
        // Prevent Ctrl+Shift+R/Cmd+Shift+R (hard refresh)
        if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'R') {
          event.preventDefault()
          return false
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('unload', handleUnload)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('unload', handleUnload)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [state.isActive])

  // Wait for screen sharing before starting interview
  useEffect(() => {
    const startInterviewProcess = async () => {
      // Only start if we haven't started yet, have basic requirements, and have screen sharing
      if (!isAutoStarted && candidateName && !state.isActive && streams.screen && streams.camera) {
        console.log('Starting interview with screen sharing...', { candidateName })
        setIsAutoStarted(true)

        // Store interview start time for duration calculation
        localStorage.setItem('interview-start-time', Date.now().toString())

        // Start the actual interview hook
        startInterviewHook()

        // Start VAD and recording
        try {
          await startVAD()

          // Create canvas-based combined video stream
          const combinedStream = await createCombinedVideoStream(streams.screen, streams.camera)
          console.log('Created canvas-based combined video stream with tracks:', combinedStream.getTracks().length)

          // Start single video recording for entire interview
          startSingleVideoRecording(combinedStream)
          console.log('Started single video recording for entire interview')

          toast.success('Interview started successfully!')
        } catch (error) {
          console.error('Failed to start interview:', error)
          toast.error('Failed to start interview. Please try again.')
        }
      }
    }

    // Start immediately if we have all requirements
    if (streams.screen && streams.camera && candidateName && !state.isActive && !isAutoStarted) {
      startInterviewProcess()
    }
  }, [candidateName, isAutoStarted, state.isActive, startInterviewHook, startVAD, streams.camera, streams.screen])

  // Request screen sharing if not available
  useEffect(() => {
    const requestScreenIfNeeded = async () => {
      if (!streams.screen && streams.camera && candidateName && !isRequestingScreenShare && !isAutoStarted) {
        console.log('Screen sharing required for interview, requesting permission...')
        setIsRequestingScreenShare(true)
        try {
          await requestScreenShare()
          console.log('Screen sharing permission granted')
          // Removed toast here - will show success when interview starts
        } catch (screenError) {
          console.warn('Screen sharing permission denied:', screenError)
          const message = screenError instanceof Error ? screenError.message : ''
          toast.error(
            message.includes('Entire Screen') || message.includes('Full screen')
              ? message
              : 'Screen sharing is required to start the interview. Please refresh and try again.'
          )
        } finally {
          setIsRequestingScreenShare(false)
        }
      }
    }

    // Only request if we have camera but no screen
    if (streams.camera && !streams.screen && !isRequestingScreenShare && !isAutoStarted) {
      requestScreenIfNeeded()
    }
  }, [streams.camera, streams.screen, candidateName, isRequestingScreenShare, isAutoStarted, requestScreenShare])

  // Strict fullscreen enforcement during interview
  useEffect(() => {
    let fullscreenViolations = 0
    let fullscreenWarningTimeout: NodeJS.Timeout | null = null
    let forceFullscreenInterval: NodeJS.Timeout | null = null
    let violationWarningShown = false
    let hasInitiallyEnteredFullscreen = false // Track initial fullscreen entry

    const enterFullscreen = async (isInitialEntry = false) => {
      if (state.isActive && document.documentElement.requestFullscreen) {
        try {
          await document.documentElement.requestFullscreen()
          console.log('Entered fullscreen mode for interview')
          violationWarningShown = false // Reset flag when successfully entering fullscreen
          if (isInitialEntry) {
            hasInitiallyEnteredFullscreen = true
          }
        } catch (error) {
          console.warn('Could not enter fullscreen mode:', error)
          // Only show error toast for non-initial entries or if we've never successfully entered fullscreen
          if (state.isActive && !violationWarningShown && (!isInitialEntry || hasInitiallyEnteredFullscreen)) {
            toast.error('Fullscreen mode is required for this interview. Please allow fullscreen access.')
            violationWarningShown = true
          }
        }
      }
    }

    const exitFullscreen = async () => {
      if (!state.isActive && document.fullscreenElement) {
        try {
          await document.exitFullscreen()
          console.log('Exited fullscreen mode')
        } catch (error) {
          console.warn('Could not exit fullscreen mode:', error)
        }
      }
    }

    const handleFullscreenChange = () => {
      if (state.isActive && !document.fullscreenElement) {
        // Only count as violation if we've successfully entered fullscreen at least once
        if (hasInitiallyEnteredFullscreen) {
          fullscreenViolations++
          console.warn(`Fullscreen violation #${fullscreenViolations}`)

          // Clear any existing timeout
          if (fullscreenWarningTimeout) {
            clearTimeout(fullscreenWarningTimeout)
          }

          if (fullscreenViolations >= 3) {
            // After 3 violations, end the interview
            toast.error('Interview terminated due to repeated fullscreen violations')
            setTimeout(() => {
              endInterview('Interview terminated - repeated fullscreen violations')
            }, 2000)
            return
          }

          // Show escalating warnings with required dialog consent
          if (fullscreenViolations === 1) {
            const userResponse = confirm('⚠️ FULLSCREEN VIOLATION WARNING\n\nYou have exited fullscreen mode during the interview. This is not allowed and is being monitored.\n\nYou must return to fullscreen mode immediately to continue.\n\nClick OK to acknowledge and continue the interview.')
            if (!userResponse) {
              // If user doesn't consent, end interview immediately
              toast.error('Interview terminated - fullscreen compliance required')
              endInterview('Interview terminated - user refused fullscreen compliance')
              return
            }
          } else if (fullscreenViolations === 2) {
            const userResponse = confirm('🚨 FINAL WARNING - INTERVIEW TERMINATION IMMINENT\n\nThis is your FINAL WARNING. You have violated fullscreen requirements twice.\n\nOne more violation will automatically terminate your interview.\n\nYou MUST stay in fullscreen mode to continue.\n\nClick OK to acknowledge this final warning.')
            if (!userResponse) {
              // If user doesn't consent to final warning, end interview immediately
              toast.error('Interview terminated - fullscreen compliance required')
              endInterview('Interview terminated - user refused final fullscreen warning')
              return
            }
          }

          // Attempt to re-enter fullscreen immediately
          enterFullscreen()

          // Set a timeout to try again if user doesn't comply
          fullscreenWarningTimeout = setTimeout(() => {
            if (state.isActive && !document.fullscreenElement) {
              enterFullscreen()
              if (fullscreenViolations < 3) {
                toast.warning('Fullscreen mode is mandatory for this interview')
              }
            }
          }, 3000)
        } else {
          // Initial fullscreen setup - don't count as violation, just try to enter again
          console.log('Initial fullscreen setup detected, attempting to enter fullscreen...')
          enterFullscreen(true)
        }
      }
    }

    // Enhanced keyboard event blocking during interview
    const handleKeyDown = (event: KeyboardEvent) => {
      if (state.isActive) {
        // Prevent Escape key (exits fullscreen)
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          toast.warning('Fullscreen mode cannot be disabled during the interview')
          return false
        }
        
        // Prevent F11 (toggle fullscreen)
        if (event.key === 'F11') {
          event.preventDefault()
          event.stopPropagation()
          return false
        }

        // Prevent Alt+Tab (task switching)
        if (event.altKey && event.key === 'Tab') {
          event.preventDefault()
          event.stopPropagation()
          toast.warning('Task switching is not allowed during the interview')
          return false
        }

        // Prevent Windows/Cmd key
        if (event.key === 'Meta' || event.keyCode === 91 || event.keyCode === 92) {
          event.preventDefault()
          event.stopPropagation()
          return false
        }

        // Prevent Ctrl+Shift+I (DevTools)
        if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'I') {
          event.preventDefault()
          event.stopPropagation()
          return false
        }

        // Prevent F12 (DevTools)
        if (event.key === 'F12') {
          event.preventDefault()
          event.stopPropagation()
          return false
        }
      }
    }

    // Monitor window focus to detect when user switches away
    const handleWindowBlur = () => {
      if (state.isActive) {
        console.warn('Window lost focus during interview')
        setTimeout(() => {
          if (state.isActive && !document.hasFocus()) {
            toast.warning('Please return focus to the interview window')
          }
        }, 2000)
      }
    }

    const handleWindowFocus = () => {
      if (state.isActive && !document.fullscreenElement && hasInitiallyEnteredFullscreen) {
        // If window regains focus but not in fullscreen, try to re-enter (only if we've initially entered)
        enterFullscreen()
      }
    }

    // Enter fullscreen when interview starts
    if (state.isActive) {
      enterFullscreen(true) // Mark as initial entry
      
      // Set up periodic fullscreen enforcement (every 5 seconds)
      forceFullscreenInterval = setInterval(() => {
        if (state.isActive && !document.fullscreenElement) {
          console.log('Periodic fullscreen enforcement check')
          enterFullscreen()
        }
      }, 5000)
    } else {
      // Interview ended - cleanup all fullscreen enforcement
      exitFullscreen()
      fullscreenViolations = 0
      violationWarningShown = false
      
      if (fullscreenWarningTimeout) {
        clearTimeout(fullscreenWarningTimeout)
        fullscreenWarningTimeout = null
      }
      
      if (forceFullscreenInterval) {
        clearInterval(forceFullscreenInterval)
        forceFullscreenInterval = null
      }
      
      console.log('Interview ended - fullscreen tracking reset')
    }

    // Add all event listeners with capture phase for key events
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('keydown', handleKeyDown, true) // Use capture phase
    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('focus', handleWindowFocus)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('focus', handleWindowFocus)
      
      if (fullscreenWarningTimeout) {
        clearTimeout(fullscreenWarningTimeout)
      }
      if (forceFullscreenInterval) {
        clearInterval(forceFullscreenInterval)
      }
    }
  }, [state.isActive, endInterview])

  // Auto-scroll to bottom when new messages arrive (but not on initial load)
  useEffect(() => {
    // Only auto-scroll if there are multiple messages (not just the initial one)
    if (state.messages.length > 1 && messagesContainerRef.current) {
      // Scroll the container to the bottom instead of using scrollIntoView
      const container = messagesContainerRef.current
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [state.messages])

  // Refocus textarea after AI responses in text mode
  useEffect(() => {
    // Only focus if in text mode, interview is active, not loading, and not completed
    if (inputMode === 'text' && state.isActive && !isLoading && !state.isCompleted && textareaRef.current) {
      // Use a small delay to ensure the textarea is re-enabled after loading completes
      const focusTimeout = setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
      
      return () => clearTimeout(focusTimeout)
    }
  }, [isLoading, inputMode, state.isActive, state.isCompleted])

  // Timer for interview duration - using state.duration from useInterview hook
  // No need for separate timer as useInterview handles it

  // startInterview logic has been moved to the auto-start useEffect above

  // Removed startScreenSharing - all permissions handled on welcome page

  // Handle interview completion
  useEffect(() => {
    const handleCompletion = async () => {
      if (state.isCompleted) {
        console.log('Interview completed, stopping recording and preparing to redirect...')
        console.log('Current auth state:', { isAuthenticated })
        console.log('Current location:', window.location.href)

        // Stop single video recording and process interview data
        try {
          console.log('Interview completed, stopping recording and processing...')
          
          // Stop single video recording and show upload modal
          await stopSingleVideoRecordingAndGenerateReport()
          console.log('Video recording stopped and processing completed successfully')
          
          // Clean up and navigate (this should happen inside stopSingleVideoRecordingAndGenerateReport on success)
          // Navigation is handled by the upload modal completion, not here
          console.log('Interview completion processing finished')
          
          // Don't clear session ID here - only clear it after successful completion in the modal
          
        } catch (error) {
          console.error('Error stopping recording or processing interview data:', error)
          toast.error('Failed to process interview data. Please try again.')
          
          // Don't navigate away - let user retry via the upload modal
          console.log('Processing failed, upload modal should be showing for retry')
        }
      }
    }

    handleCompletion()
  }, [state.isCompleted, navigate, stopVAD, stopAllStreams, competencyRecording])

  // Set up callback to end interview when screen sharing stops
  useEffect(() => {
    setOnScreenSharingEnded(() => {
      if (state.isActive && !state.isCompleted) {
        console.log('Screen sharing ended, automatically ending interview...')
        toast.warning('Screen sharing stopped. Interview will be ended automatically.')
        // End the interview with specific reason
        endInterview('Automatic end - screen sharing disconnected')
      }
    })
  }, [state.isActive, state.isCompleted, endInterview, setOnScreenSharingEnded])

  // Auto-scroll competency areas to show current competency at top (DISABLED)
  // useEffect(() => {
  //   if (currentCompetencyRef.current && competencyContainerRef.current) {
  //     const container = competencyContainerRef.current
  //     const currentElement = currentCompetencyRef.current
  //     
  //     // Scroll so the current competency appears at the top of the container
  //     const containerTop = container.scrollTop
  //     const elementTop = currentElement.offsetTop - container.offsetTop
  //     
  //     // Add smooth scroll behavior
  //     container.scrollTo({
  //       top: elementTop,
  //       behavior: 'smooth'
  //     })
  //   }
  // }, [state.currentCompetencyIndex])

  // Handle competency transitions for video recording - DISABLED
  // useEffect(() => {
  //   if (state.isActive && competencyRecording.isRecording && state.currentCompetency) {
  //     const currentCompetencyName = state.currentCompetency.name
  //     const recordingCompetency = competencyRecording.currentCompetency
      
  //     // If the interview competency has changed from the recording competency, switch
  //     if (recordingCompetency && currentCompetencyName !== recordingCompetency) {
  //       console.log(`Competency transition detected: ${recordingCompetency} -> ${currentCompetencyName}`)
  //       competencyRecording.switchCompetency(currentCompetencyName)
  //     }
  //   }
  // }, [state.currentCompetency?.name, state.isActive, competencyRecording])

  // No permission checking needed - all handled on welcome page

  // Format time helper
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Get timer color and style based on remaining time
  const getTimerStyle = (remainingSeconds: number) => {
    if (remainingSeconds <= 120) { // 2 minutes or less - danger red
      return {
        color: 'text-red-600',
        weight: 'font-bold',
        bg: 'bg-red-50',
        border: 'border-red-200',
        pulse: 'animate-pulse'
      }
    } else if (remainingSeconds <= 300) { // 5 minutes or less - warning orange
      return {
        color: 'text-orange-600',
        weight: 'font-semibold',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        pulse: ''
      }
    } else { // Normal - gray
      return {
        color: 'text-gray-600',
        weight: 'font-medium',
        bg: '',
        border: '',
        pulse: ''
      }
    }
  }

  // Handle sending messages
  const handleSendMessage = async () => {
    if (!messageInput.trim() || isLoading) return
    
    const message = messageInput.trim()
    setMessageInput('')
    
    try {
      await sendMessage(message)
    } catch (error) {
      console.error('Failed to send message:', error)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      handleSendMessage()
    }
  }

  // Calculate overall progress using state from useInterview
  const overallProgress = state.overallProgress

  // Redirect to welcome if no session ID (but don't crash the component)
  useEffect(() => {
    if (!sessionId) {
      console.log('No session ID found, redirecting to welcome page')
      navigate('/welcome', { replace: true })
    }
  }, [sessionId, navigate])

  // Early return if no session ID to prevent hooks violation
  if (!sessionId) {
    return (
      <div className={clsx('min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-snwolley-green-50', fontClass)}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to welcome page...</p>
        </div>
      </div>
    )
  }

  // Removed permission screen - all permissions handled on welcome page

  return (
    <div className={clsx('min-h-screen bg-gradient-to-br from-gray-50 via-white to-snwolley-green-50', fontClass)}>
      {/* Header - Original Snwolley styling */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-snwolley rounded-xl flex items-center justify-center mr-3">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Snwolley Interview</h1>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-4">👤 {candidateName}</span>
                  <span className="mr-4">💼 {position}</span>
                  {(() => {
                    const timerStyle = getTimerStyle(state.duration)
                    return (
                      <div className={clsx(
                        "flex items-center px-2 py-1 rounded-md",
                        timerStyle.bg,
                        timerStyle.border && `border ${timerStyle.border}`,
                        timerStyle.pulse
                      )}>
                        <ClockIcon className={clsx("w-4 h-4 mr-1", timerStyle.color)} />
                        <span className={clsx(timerStyle.color, timerStyle.weight)}>
                          {formatTime(state.duration)}
                        </span>
                      </div>
                    )
                  })()}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Manual Completion Button */}
              {!state.isCompleted && (
                <Button
                  onClick={async () => {
                    if (confirm('Are you sure you want to end the interview? This action cannot be undone.')) {
                      console.log('Manual interview end requested - button click')
                      endInterview('Manual end by candidate - button click')
                    }
                  }}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                  size="sm"
                >
                  End Interview
                </Button>
              )}

              {/* Recording Status */}
              {videoRecordingRef.current.mediaRecorder && videoRecordingRef.current.mediaRecorder.state === 'recording' && (
                <div className={clsx(
                  "flex items-center text-sm font-medium",
                  isTabVisible ? "text-red-600" : "text-orange-600"
                )}>
                  <div className={clsx(
                    "w-3 h-3 rounded-full animate-pulse mr-2",
                    isTabVisible ? "bg-red-500" : "bg-orange-500"
                  )}></div>
                  <span>
                    {isTabVisible ? 'Recording Interview' : 'Recording (Tab Hidden!)'}
                  </span>
                </div>
              )}

              {/* VAD status (speech mode labels disabled — text-only) */}
              <div className={clsx(
                "flex items-center px-3 py-1 rounded-full text-xs font-medium",
                isSpeaking 
                  ? "bg-green-100 text-green-800" 
                  : "bg-gray-100 text-gray-600"
              )}>
                <MicrophoneIcon className="w-4 h-4 mr-1" />
                {isSpeaking ? 'Speaking' : 'Listening'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-6">
          {/* Left Sidebar - Camera and Screen Recording */}
          <div className="space-y-4">
            {/* Camera Feed */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Camera</h3>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                {streams.camera ? (
                  <video
                    key="camera-stream" // Prevent re-mounting
                    ref={(video) => {
                      if (video && streams.camera && video.srcObject !== streams.camera) {
                        video.srcObject = streams.camera
                        video.play().catch(console.warn)
                      }
                    }}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover rounded-lg"
                    style={{ backgroundColor: '#000' }}
                  />
                ) : (
                  <div className="text-gray-400 text-center">
                    <UserIcon className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-xs">Camera not available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Screen Recording */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Screen Recording</h3>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                {streams.screen ? (
                  <video
                    key="screen-stream" // Prevent re-mounting
                    ref={(video) => {
                      if (video && streams.screen && video.srcObject !== streams.screen) {
                        video.srcObject = streams.screen
                        video.play().catch(console.warn)
                      }
                    }}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover rounded-lg"
                    style={{ backgroundColor: '#000' }}
                  />
                ) : (
                  <div className="text-gray-400 text-center">
                    <ComputerDesktopIcon className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-xs">Screen not available</p>
                  </div>
                )}
              </div>
            </div>

            {/* System Status */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">System Status</h3>
              <div className="space-y-2">
                <div className="flex items-center text-xs">
                  <div className={clsx(
                    "w-2 h-2 rounded-full mr-2",
                    streams.camera ? "bg-green-500" : "bg-red-500"
                  )}></div>
                  <span className="text-gray-700">Camera</span>
                </div>
                <div className="flex items-center text-xs">
                  <div className={clsx(
                    "w-2 h-2 rounded-full mr-2",
                    streams.microphone ? "bg-green-500" : "bg-red-500"
                  )}></div>
                  <span className="text-gray-700">Microphone</span>
                </div>
                <div className="flex items-center text-xs">
                  <div className={clsx(
                    "w-2 h-2 rounded-full mr-2",
                    streams.screen ? "bg-green-500" : "bg-red-500"
                  )}></div>
                  <span className="text-gray-700">Screen</span>
                </div>
                {videoRecordingRef.current.mediaRecorder && videoRecordingRef.current.mediaRecorder.state === 'recording' && (
                  <div className="flex items-center text-xs">
                    <div className={clsx(
                      "w-2 h-2 rounded-full mr-2 animate-pulse",
                      isTabVisible ? "bg-red-500" : "bg-orange-500"
                    )}></div>
                    <span className={clsx(
                      "text-gray-700",
                      !isTabVisible && "font-medium"
                    )}>
                      {isTabVisible ? 'Recording' : 'Recording (Tab Hidden!)'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="lg:col-span-4">
            <div className="card h-[700px] flex flex-col">
              {/* Chat Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {state.currentCompetency?.displayName || 'Initializing...'}
                    </h2>
                    <div className="text-sm text-gray-600">
                      Talk to Snwolley
                    </div>
                  </div>
                  <ChartBarIcon className="w-6 h-6 text-snwolley-green-600" />
                </div>
              </div>

              {/* Chat Messages */}
              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-6 space-y-4 scroll-smooth"
                style={{ scrollBehavior: 'smooth' }}
              >
                {state.messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-400">
                      {!streams.screen ? (
                        <>
                          <ComputerDesktopIcon className="w-12 h-12 mx-auto mb-4 text-yellow-600" />
                          <p className="text-lg text-gray-600">Please share your screen again</p>
                          <p className="text-sm text-gray-500 mb-4">
                            {isRequestingScreenShare
                              ? 'Requesting screen sharing permission...'
                              : 'Please share your screen again to begin the interview.'
                            }
                          </p>
                          {isRequestingScreenShare && (
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-snwolley-green-600"></div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-12 h-12 mx-auto mb-4 text-snwolley-green-600" />
                          <p className="text-lg text-gray-600">Interview starting...</p>
                          <p className="text-sm text-gray-500">Please wait while Snwolley prepares your first question.</p>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <AnimatePresence>
                    {state.messages.map((message) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={clsx(
                          "flex",
                          message.role === 'user' ? "justify-end" : "justify-start"
                        )}
                      >
                        <div className={clsx(
                          "max-w-lg rounded-xl px-4 py-3",
                          message.role === 'user' 
                            ? "bg-snwolley-green-600 text-white" 
                            : "bg-gray-100 text-gray-900"
                        )}>
                          {message.role === 'assistant' && (
                            <div className="flex items-center mb-2">
                              <div className="w-5 h-5 bg-gradient-snwolley rounded-full flex items-center justify-center mr-2">
                                <SparklesIcon className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-xs font-medium text-gray-600">Snwolley AI</span>
                            </div>
                          )}
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {message.content}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="bg-gray-100 rounded-xl px-4 py-3 max-w-lg">
                      <div className="flex items-center mb-2">
                        <div className="w-5 h-5 bg-gradient-snwolley rounded-full flex items-center justify-center mr-2">
                          <SparklesIcon className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-xs font-medium text-gray-600">Snwolley AI</span>
                      </div>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area - Only show for text mode */}
              {inputMode === 'text' && !state.isCompleted && (
                <div className="border-t border-gray-200 p-4">
                  <div className="flex space-x-3">
                    <div className="flex-1">
                      <textarea
                        ref={textareaRef}
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your response here..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-500 resize-none focus:ring-2 focus:ring-snwolley-green-500 focus:border-transparent"
                        rows={2}
                        disabled={isLoading}
                      />
                    </div>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim() || isLoading}
                      variant="primary"
                      className="bg-gradient-snwolley hover:shadow-snwolley-md px-4 py-2 mb-1.5 rounded-lg flex items-center space-x-2"
                    >
                      <PaperAirplaneIcon className="w-4 h-4" />
                      <span>Send</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Speech Mode Interface — disabled (text-only interviews)
              {inputMode === 'speech' && !state.isCompleted && (
                <div className="border-t border-gray-200 p-4 space-y-4">
                  ... speech recording controls ...
                </div>
              )}
              */}

              {/* Interview Complete */}
              {state.isCompleted && (
                <div className="border-t border-gray-200 p-4">
                  <div className="text-center text-gray-600">
                    <SparklesIcon className="w-8 h-8 mx-auto mb-2 text-snwolley-green-600" />
                    <p className="text-lg font-medium">Interview Completed!</p>
                    <p className="text-sm">Redirecting to completion page...</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Progress */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overall Progress */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Progress</h3>
              <ProgressBar 
                value={overallProgress}
                className="mb-4"
              />
              <p className="text-sm text-gray-600">
                {Math.round(overallProgress)}% complete
              </p>
            </div>

            {/* Competency Areas */}
            <div className="card h-[500px] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Competency Areas</h3>
              </div>
              <div 
                ref={competencyContainerRef}
                className="flex-1 overflow-y-auto p-6 space-y-3 scroll-smooth"
              >
                {state.competencies.map((comp, index) => (
                  <div
                    key={comp.id}
                    ref={index === state.currentCompetencyIndex ? currentCompetencyRef : null}
                    className={clsx(
                      'p-3 rounded-lg transition-colors',
                      index === state.currentCompetencyIndex
                        ? 'bg-snwolley-green-100 border border-snwolley-green-300'
                        : 'bg-gray-50'
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={clsx(
                        'text-xs font-medium',
                        index === state.currentCompetencyIndex 
                          ? 'text-snwolley-green-800' 
                          : 'text-gray-700'
                      )}>
                        {comp.displayName}
                        {/* Debug info in development */}
                        {import.meta.env.DEV && (
                          <span className="ml-1 text-xs text-gray-400">
                            [{index}]
                          </span>
                        )}
                      </span>
                      <div className="flex items-center space-x-1">
                        {comp.completed && (
                          <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        )}
                        {index === state.currentCompetencyIndex && (
                          <div className="w-2 h-2 bg-snwolley-green-500 rounded-full animate-pulse" />
                        )}
                      </div>
                    </div>
                    
                    {/* Dynamic progress bar */}
                    <div className="relative">
                      <ProgressBar 
                        value={Math.min(100, (comp.questionsAsked / 3) * 100)} // Show progress against target (3 questions)
                        size="sm"
                        className={clsx(
                          comp.questionsAsked > 3 ? 'opacity-75' : ''
                        )}
                      />
                      {/* Extended questioning indicator */}
                      {comp.questionsAsked > 3 && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-yellow-200 opacity-30 rounded-full" />
                      )}
                    </div>
                    
                    {/* Question count with visual indicators */}
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className={clsx(
                        comp.questionsAsked > 3 ? 'text-yellow-700 font-medium' : 'text-gray-600'
                      )}>
                        {comp.questionsAsked <= 3 
                          ? `${comp.questionsAsked}/3 questions`
                          : `${comp.questionsAsked}/5 questions (extended)`
                        }
                      </span>
                      {comp.questionsAsked > 3 && (
                        <span className="text-yellow-600 text-xs">
                          Extended
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Upload Progress Modal */}
      <UploadProgressModal
        isOpen={isUploadModalOpen}
        videoUploadStatus={videoUploadStatus}
        reportGenerationStatus={reportGenerationStatus}
        currentAttempt={currentUploadAttempt}
        maxAttempts={3}
        errorMessage={uploadError}
        onRetry={handleRetryProcessing}
      />
    </div>
  )
}

export default InterviewPage